import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { BiBlock } from "react-icons/bi";
import { FiPlus } from "react-icons/fi";

import {
  api,
  PaymentReconciliation,
  Product,
  ProductSaleItem,
  ProductTypes,
  UpsellPackage,
  UpsellPackagedProduct,
} from "../../../../logic/api";

import flex from "../../../../styles/flex";

import { SessionContext } from "../../../../utility/useSession";
import { formatCurrency } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";
import { ReleaseFlag, useReleaseManagement } from "../../../../utility/feature-management";

import ProductPickerDialog from "../../../../components/pickers/productPicker/ProductPickerDialog";
import ConfirmationDialog from "../../../../components/notifications/confirmationDialog/ConfirmationDialog";
import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import ProductsForBookingTreeViewLoader, { IProductsForBookingTreeViewLoaderProps } from "../../../../components/treeView/loaders/ProductsForBookingTreeViewLoader";
import ProductsTreeViewLoader, { IProductsTreeViewLoaderProps } from "../../../../components/treeView/loaders/ProductsTreeViewLoader";
import ProductQtyDialog from "./ProductQtyDialog";
import AlertDialog from "../../../../components/notifications/alertDialog/AlertDialog";
import { useTranslation } from "react-i18next";

interface IEditProductsDialog {
  bookingId: number;
  returnDate?: string;
  saleItems: ProductSaleItem[];
  reconciliations?: PaymentReconciliation[];
  show: boolean;
  onRequestUpdate: () => void;
  onClose: () => void;
}

const EditProductsDialog: React.FunctionComponent<IEditProductsDialog> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [saleItems, setSaleItems] = useState<ProductSaleItem[]>([]);
  const [showProductPickerDialog, setShowProductPickerDialog] = useState(false);
  const [showVoidConfirmation, setShowVoidConfirmation] = useState(false);
  const [showVoidUpsellConfirmation, setShowVoidUpsellConfirmation] = useState(false);
  const [showVoidAlert, setShowVoidAlert] = useState(false);
  const [showQtyDialog, setShowQtyDialog] = useState(false);
  const [qtyDialogSelectedProduct, setQtyDialogSelectedProduct] = useState<Product | null>(null);
  const [
    currentSaleItem,
    setCurrentSaleItem,
  ] = useState<ProductSaleItem | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const { localizeDate } = useDate();

  const isAvailableProductsByBookingReleased = useReleaseManagement(ReleaseFlag.releaseAvailableBookingProducts);
  const isAllowAddingMultipleValetProductsReleased = useReleaseManagement(ReleaseFlag.releaseAllowAddingMultipleValetProducts);

  const Loader: React.FC<IProductsForBookingTreeViewLoaderProps | IProductsTreeViewLoaderProps> = isAvailableProductsByBookingReleased
    ? ProductsForBookingTreeViewLoader
    : ProductsTreeViewLoader;
  const constructor = (props: IProductsForBookingTreeViewLoaderProps | IProductsTreeViewLoaderProps) => {
    const renderItem = (item: Product) => <ProductName product={item} />
    return <Loader renderItem={renderItem} {...props} />
  }

  const treeViewLoader = {
    constructor: constructor,
    flags: {
      bookingId: props.bookingId,
      returnDate: props.returnDate,
    },
  };

  const voidProduct = api.hooks.productSale.voidProduct();
  const applyProduct = api.hooks.productSale.applyProduct();
  const applyUpsell = api.hooks.upsell.applyUpsellPackage();

  const checkSaleItemPayments = (saleItem: ProductSaleItem) => {
    if (!props.reconciliations) return false;

    const saleItemReconciliations = props.reconciliations.filter(reconciliation => reconciliation.productSaleItem.id === saleItem.id);
    const saleItemTotalPayments = saleItemReconciliations.reduce((total, sir) => total += Number(sir.amount), 0);

    return saleItemTotalPayments > 0;
  }

  const filterSaleItems = (saleItems: ProductSaleItem[]) => {
    return saleItems.filter((p) => {
      return (
        p.voided != true &&
        p.productType != ProductTypes.overstay &&
        p.productType != ProductTypes.walkIn
      );
    });
  };

  const removeSaleItem = (item: ProductSaleItem) => {
    setCurrentSaleItem(item);
    if (item.isUpsell == true) {
      setShowVoidUpsellConfirmation(true);
    } else {
      setShowVoidConfirmation(true);
    }
  };

  const addProduct = async (product: Product, qty: number) => {

    applyProduct.send(
      session.current.context,
      {
        bookingId: props.bookingId,
        productId: product.id,
        saleDate: localizeDate(new Date()).toISO() ?? "",
        qty: qty,
      },
      (updatedSaleItems) => {
        if (updatedSaleItems != null) {
          //  setSaleItems(filterSaleItems(updatedSaleItems));
          props.onRequestUpdate();
        }
      }
    );
  };
  const applyUpsellPackage = async (upsellPackage: UpsellPackage, product: UpsellPackagedProduct) => {
    if (product.ref == null || upsellPackage.id == null) {
      return;
    }
    applyUpsell.send(
      session.current.context,
      {
        bookingId: props.bookingId,
        saleDate: localizeDate(new Date()).toISO() ?? "",
        packagedProductref: product.ref,
        upsellPackageId: upsellPackage.id,
      },
      (updatedSaleItems) => {
        if (updatedSaleItems != null) {
          //  setSaleItems(filterSaleItems(updatedSaleItems));
          props.onRequestUpdate();
        }
      }
    );
  };


  const confirmedRemoveSaleItem = async () => {
    if (currentSaleItem == null) {
      return;
    }

    voidProduct.send(
      session.current.context,
      {
        bookingId: props.bookingId,
        saleItemId: currentSaleItem.id,
        voidDate: localizeDate(new Date()).toISO() ?? "",
      },
      (updatedSaleItems) => {
        if (updatedSaleItems != null) {
          //  setSaleItems(filterSaleItems(updatedSaleItems));
          props.onRequestUpdate();
        }
        setCurrentSaleItem(null);
      }
    );
  };

  useEffect(() => {
    if (props.show == true) {
      setSaleItems(filterSaleItems(props.saleItems));
    }
  }, [props.saleItems]);

  return (
    <Dialog maxWidth={"md"} open={props.show}>
      <DialogTitle>{t("booking.labels.editProducts")}</DialogTitle>
      <DialogContent>
        <ErrorDialog err={err}></ErrorDialog>
        <ErrorDialog err={voidProduct.err}></ErrorDialog>
        <ErrorDialog err={applyProduct.err}></ErrorDialog>
        <ErrorDialog err={applyUpsell.err}></ErrorDialog>
        <ProgressDialog status={voidProduct.status}></ProgressDialog>
        <ProgressDialog status={applyProduct.status}></ProgressDialog>
        <ProgressDialog status={applyUpsell.status}></ProgressDialog>


        {qtyDialogSelectedProduct != null && (
          <ProductQtyDialog saleItems={saleItems} initialQty={1} product={qtyDialogSelectedProduct} show={showQtyDialog} onClose={(result) => {


            setShowQtyDialog(false);

            if (qtyDialogSelectedProduct != null && result != null) {
              setShowProductPickerDialog(false);
              addProduct(qtyDialogSelectedProduct, result);
            } else {
              setShowProductPickerDialog(true);
            }

          }}></ProductQtyDialog>
        )}

        <ProductPickerDialog
          title={t("booking.labels.addProductBooking")}
          show={showProductPickerDialog}
          bookingId={props.bookingId}
          treeViewLoader={treeViewLoader}
          onUpsellSelect={(pkg, product) => {
            setShowProductPickerDialog(false);
            applyUpsellPackage(pkg, product);
          }}
          onClose={(product) => {
            setShowProductPickerDialog(false);
            if (product) {
              for (const p of saleItems) {
                if (p.voided != true) {
                  if (p.originalProductId == product.id && product.productType != ProductTypes.default) {
                    setErr(
                      new Error(
                        t("errors.bookingAlreadyContainsProduct", { productName: product.name })
                      )
                    );
                    return;
                  }
                  if (
                    !isAllowAddingMultipleValetProductsReleased &&
                    p.productType == product.productType &&
                    product.productType != ProductTypes.default
                  ) {
                    setErr(
                      new Error(
                        t("errors.similarProductExists", { productName: product.name, existingProductName: p.name })
                      )
                    );
                    return;
                  }
                }
              }

              if (product.productType == ProductTypes.default) {
                const prodMaxQty = product.maxQty ?? 0;
                const existingQty = saleItems
                  .filter((saleItem) => !saleItem.voided)
                  .filter((saleItem) => saleItem.originalProductId === product.id)
                  .map((saleItem) => saleItem.qty)
                  .reduce((acc, value) => acc += value, 0);

                if (existingQty >= prodMaxQty && prodMaxQty > 0) {
                  setErr(
                    new Error(
                      t("errors.productMaxQty", { existingQuantity: existingQty, productMaxQuantity: prodMaxQty })
                    )
                  );
                  return;
                }

                setShowQtyDialog(true);
                setQtyDialogSelectedProduct(product);
              } else {
                addProduct(product, 1);
              }
            }
          }}
        ></ProductPickerDialog>

        <ConfirmationDialog
          title={t("confirmationDialog.voidProduct.title")}
          message={t("confirmationDialog.voidProduct.message")}
          show={showVoidConfirmation}
          onClose={(confirmed) => {
            setShowVoidConfirmation(false);
            if (confirmed == true) {
              confirmedRemoveSaleItem();
            }
          }}
        ></ConfirmationDialog>

        <ConfirmationDialog
          title={t("confirmationDialog.voidUpsell.title")}
          message={t("confirmationDialog.voidUpsell.message")}
          show={showVoidUpsellConfirmation}
          onClose={(confirmed) => {
            setShowVoidUpsellConfirmation(false);
            if (confirmed == true) {
              confirmedRemoveSaleItem();
            }
          }}
        ></ConfirmationDialog>

        <AlertDialog
          title={t("alertDialog.cannotVoidProduct.title")}
          message={t("alertDialog.cannotVoidProduct.message")}
          show={showVoidAlert}
          onClose={() => {
            setShowVoidAlert(false);
          }}
        ></AlertDialog>

        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() => {
              setShowProductPickerDialog(true);
            }}
            variant="contained"
            color="secondary"
          >
            <FiPlus style={{ marginRight: 5 }}></FiPlus>{t("input.labels.addProduct")}
          </Button>
        </div>
        <div style={{ width: 500, marginBottom: 50, marginTop: 15 }}>
          <div
            style={{
              border: "solid 1px #eee",
              borderTop: saleItems.length == 0 ? "solid 1px #eee" : "none",
              borderRadius: 10,
            }}
          >
            {saleItems.length == 0 && (
              <div
                style={{ marginTop: 20, marginBottom: 20, textAlign: "center" }}
              >
                <div>
                  <span style={{ color: "gray" }}>{t("input.validations.noProduct")}</span>
                </div>
              </div>
            )}
            {saleItems.map((item, key) => {
              const cost = formatCurrency(item.rate * item.qty, session);
              const rate = formatCurrency(item.rate, session);

              const hasSaleItemBeenPaid = checkSaleItemPayments(item);

              return (
                <div
                  style={{
                    borderTop: "solid 1px #eee",
                    padding: 5,
                    color:
                      item.productType == ProductTypes.overstay
                        ? "red"
                        : "black",
                  }}
                  key={key}
                >
                  <div style={flex.flexRows}>
                    <div style={flex.flexAuto}>
                      <div
                        style={{
                          width: 100,
                          marginTop: 15,
                          textAlign: "center",
                        }}
                      >
                        {item.prePaid != true && <b>{cost}</b>}
                        {item.prePaid == true && (
                          <small>
                            {" "}
                            <b style={{ color: "green" }}>{t("booking.labels.preBooked")}</b>
                          </small>
                        )}

                      </div>
                    </div>
                    <div style={flex.flexAuto}>
                      <div
                        style={{
                          width: 150,
                          marginTop: 15,
                          textAlign: "center",
                        }}
                      >
                        {item.prePaid == true ? (
                          <small>
                            <b>x{item.qty}</b>
                          </small>
                        ) : (
                          <small>
                            <b>x{item.qty}</b> @ {rate}
                          </small>
                        )}
                      </div>
                    </div>
                    <div style={flex.flexFill}>
                      <div style={{ marginTop: 15 }}>
                        <b>{item.name}</b>
                      </div>
                      {(item.isUpsell == true) && (
                        <div>
                          <i style={{ color: "blue" }}>{t("booking.labels.upsellPackage", { packageName: item.upsellPackageName ?? "Unknown" })}</i>

                        </div>
                      )}
                    </div>

                    <div style={flex.flexAuto}>
                      {(item.prePaid == true ||
                        (item.packageCode != null && item.packageCode != "" && item.packageCode != "UPSELL")) ==
                        false && (
                          <IconButton
                            title={t("booking.labels.void")}
                            onClick={() => {
                              !hasSaleItemBeenPaid ? removeSaleItem(item) : setShowVoidAlert(true);
                            }}
                          >
                            <BiBlock color="red"></BiBlock>
                          </IconButton>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.onClose();
          }}
        >
          {t("general.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductName: React.FunctionComponent<{ product: Product }> = ({ product }) => {
  const session = useContext(SessionContext);

  const stdRate = Number(product.stdRate) || 0;
  let price = "";
  if (!product.hasChildren) {
    price = stdRate
      ? formatCurrency(stdRate, session) || ""
      : "Free";
  }

  return <>
    <div style={{ fontWeight: "bold" }}>{product.name}</div>
    <div style={{ fontSize: "smaller" }}>{price}</div>
  </>
}

EditProductsDialog.propTypes = {
  bookingId: propTypes.number.isRequired,
  saleItems: propTypes.any.isRequired,
  onClose: propTypes.func.isRequired,
  onRequestUpdate: propTypes.func.isRequired,
  show: propTypes.bool.isRequired,
};

export default EditProductsDialog;
